import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import ActivityLogs from '../components/ActivityLogs';
import { StatsGrid, StatCard, LineChartWidget, BarChartWidget, PieChartWidget, AnalyticsGrid, generateMockData } from '../components/Charts';
import CreditTransferModal from '../components/CreditTransfer';

const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function ClientAdminDashboard() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'users', label: 'Users', icon: '👥' },
    { id: 'content', label: 'Content', icon: '📄' },
    { id: 'packages', label: 'Packages', icon: '📦', hasSubMenu: true },
    { id: 'analytics', label: 'Analytics', icon: '📈', hasSubMenu: true },
    { id: 'usage', label: 'Usage', icon: '💾' },
    { id: 'integrations', label: 'Integrations', icon: '🔗' },
    { id: 'billing', label: 'Billing', icon: '💳' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
  ];

  const packagesSubMenus = [
    { id: 'all-packages', label: 'All Packages' },
    { id: 'activate-package', label: 'Activate Package' },
    { id: 'package-usage', label: 'Package Usage' },
  ];

  const analyticsSubMenus = [
    { id: 'overview', label: 'Overview' },
    { id: 'usage', label: 'Usage Stats' },
  ];

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    dispatch({ type: 'auth/logout' });
    navigate('/login');
  };
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [activeSubMenu, setActiveSubMenu] = useState('all-packages');
  const [showUserModal, setShowUserModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [creditMode, setCreditMode] = useState('transfer');
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [users, setUsers] = useState([]);
  const [topupAmount, setTopupAmount] = useState(5000);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    createNewUser: true
  });
  const [settings, setSettings] = useState({
    name: '',
    contactEmail: '',
    phone: '',
    company: {}
  });

  useEffect(() => {
    if (tenantId) {
      fetchTenant();
      fetchPackages();
      fetchUsageData();
      fetchUsers();
    }
  }, [tenantId]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  // Polling for real-time data updates
  useEffect(() => {
    const pollTenant = setInterval(() => {
      fetchTenant();
    }, 10000); // 10 seconds

    const pollBilling = setInterval(() => {
      fetchBillingData();
    }, 15000); // 15 seconds

    const pollUsers = setInterval(() => {
      if (activeTab === 'users') {
        fetchUsers();
      }
    }, 15000); // 15 seconds

    const pollPackages = setInterval(() => {
      fetchPackages();
    }, 30000); // 30 seconds

    const pollUsage = setInterval(() => {
      fetchUsageData();
    }, 30000); // 30 seconds

    return () => {
      clearInterval(pollTenant);
      clearInterval(pollBilling);
      clearInterval(pollUsers);
      clearInterval(pollPackages);
      clearInterval(pollUsage);
    };
  }, [activeTab]);

  const fetchTenant = async () => {
    try {
      const res = await api.get(`/tenants/${tenantId}`);
      setTenant(res.data.data);
      setSettings({
        name: res.data.data.name,
        contactEmail: res.data.data.contactEmail,
        phone: res.data.data.phone || '',
        company: res.data.data.company || {}
      });
      fetchBillingData();
    } catch (error) {
      console.error('Failed to fetch tenant:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingData = async () => {
    try {
      setBillingLoading(true);
      const [balanceRes, txRes] = await Promise.all([
        api.get('/billing/balance'),
        api.get('/billing/transactions?limit=10')
      ]);
      setBalance(balanceRes.data.data);
      setTransactions(txRes.data.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
    } finally {
      setBillingLoading(false);
    }
  };

  const fetchPackages = async () => {
    try {
      setPackagesLoading(true);
      const res = await api.get('/packages/tenant');
      setPackages(res.data.data || []);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    } finally {
      setPackagesLoading(false);
    }
  };

  const fetchUsageData = async () => {
    try {
      const res = await api.get('/usage/stats', {
        headers: { 'x-tenant-id': tenantId }
      });
      setUsageData(res.data.data);
    } catch (error) {
      console.error('Failed to fetch usage data:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get(`/tenants/${tenantId}/users`);
      setUsers(res.data.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleTopup = async (method) => {
    try {
      const amount = topupAmount || 5000;
      let paymentMethod = method || 'razorpay';

      if (paymentMethod === 'razorpay') {
        const orderRes = await api.post('/billing/pay/razorpay/create-order', { amount });
        alert(`Razorpay Order created: ${orderRes.data.data.orderId}. Amount: ₹${amount}`);
      } else if (paymentMethod === 'stripe') {
        const intentRes = await api.post('/billing/pay/stripe/create-intent', { amount });
        alert(`Stripe Payment Intent created. Amount: ₹${amount}`);
      }

      await api.post(`/billing/balance/${tenantId || 'default'}/recharge`, { amount, paymentMethod });
      alert(`Successfully recharged ₹${amount} credits!`);
      setShowCreditModal(false);
      fetchBillingData();
    } catch (error) {
      alert('Failed to top up: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/tenants/${tenantId}/users`, {
        ...formData,
        createNewUser: true
      });
      setShowUserModal(false);
      fetchTenant();
      setFormData({ name: '', email: '', password: '', role: 'user', createNewUser: true });
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to add user');
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm('Remove this user from the client?')) return;
    try {
      await api.delete(`/tenants/${tenantId}/users/${userId}`);
      fetchTenant();
    } catch (error) {
      alert('Failed to remove user');
    }
  };

  const handleUpdateUserRole = async (userId, role) => {
    try {
      await api.patch(`/tenants/${tenantId}/users/${userId}`, { role });
      fetchTenant();
    } catch (error) {
      alert('Failed to update role');
    }
  };

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/tenants/${tenantId}`, settings);
      setShowSettingsModal(false);
      fetchTenant();
    } catch (error) {
      alert('Failed to update settings');
    }
  };

  const handleStatusToggle = async () => {
    try {
      await api.patch(`/tenants/${tenantId}/status`, { isActive: !tenant?.isActive });
      fetchTenant();
    } catch (error) {
      alert('Failed to update status');
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Left Sidebar */}
      <div className="w-64 bg-white shadow-lg flex flex-col">
        <div className="p-4 border-b">
          <h2 className="text-xl font-bold text-indigo-600">
            {tenant?.name || 'Client Admin'}
          </h2>
          <p className="text-sm text-gray-500">Organization Dashboard</p>
        </div>
        <nav className="flex-1 p-4">
          {menuItems.map((item) => (
            <div key={item.id}>
              <button
                onClick={() => {
                  if (item.hasSubMenu) {
                    setActiveTab(item.id);
                  } else {
                    setActiveTab(item.id);
                  }
                }}
                className={`w-full text-left px-4 py-3 rounded-lg mb-1 flex items-center gap-3 ${
                  activeTab === item.id
                    ? 'bg-indigo-50 text-indigo-700 border-l-4 border-indigo-600'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span>{item.icon}</span>
                <span className="font-medium">{item.label}</span>
                {item.hasSubMenu && <span className="ml-auto text-xs">▼</span>}
              </button>
              {item.id === 'packages' && activeTab === 'packages' && (
                <div className="ml-4 mt-1 mb-3">
                  {packagesSubMenus.map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => navigate(`/client-admin/${tenantId}/${sub.id === 'all-packages' ? 'packages' : sub.id === 'activate-package' ? 'available-packages' : 'package-usage'}`)}
                      className={`w-full text-left px-4 py-2 rounded text-sm mb-1 flex items-center gap-2 ${activeSubMenu === sub.id
                          ? 'bg-indigo-100 text-indigo-800'
                          : 'text-gray-500 hover:bg-gray-100'
                        }`}
                    >
                      <span className="text-xs">▸</span>
                      {sub.label}
                    </button>
                  ))}
                </div>
              )}
              {item.id === 'analytics' && activeTab === 'analytics' && (
                <div className="ml-4 mt-1 mb-3">
                  {analyticsSubMenus.map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => setActiveSubMenu(sub.id)}
                      className={`w-full text-left px-4 py-2 rounded text-sm mb-1 flex items-center gap-2 ${activeSubMenu === sub.id
                          ? 'bg-indigo-100 text-indigo-800'
                          : 'text-gray-500 hover:bg-gray-100'
                        }`}
                    >
                      <span className="text-xs">▸</span>
                      {sub.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
        <div className="p-4 border-t">
          <button
            onClick={handleLogout}
            className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 rounded flex items-center gap-2"
          >
            <span>🚪</span>
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="bg-white shadow-sm border-b px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-800">
              {menuItems.find(m => m.id === activeTab)?.label || 'Dashboard'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { setCreditMode('transfer'); setShowCreditModal(true); }} className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700">Top Up</button>
            <button onClick={() => setShowSettingsModal(true)} className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700">Settings</button>
            <button onClick={() => navigate(`/client-admin/${tenantId}/integrations`)} className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700">Integrations</button>
            <button onClick={() => navigate(`/client-admin/${tenantId}/billing`)} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">Billing</button>
            <button onClick={() => navigate(`/client-admin/${tenantId}/credits`)} className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700">Credits</button>
            <button onClick={() => navigate(`/client-admin/${tenantId}/devices`)} className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700">Devices</button>
            <button onClick={() => setShowUserModal(true)} className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700">+ Add User</button>
          </div>
        </header>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">

        {/* Stats Bar */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded shadow-sm">
              <div className="text-gray-500 text-sm">Plan</div>
              <div className="text-2xl font-bold">{tenant?.subscription?.plan || 'Free'}</div>
            </div>
            <div className="bg-white p-4 rounded shadow-sm">
              <div className="text-gray-500 text-sm">Users</div>
              <div className="text-2xl font-bold">{tenant?.users?.length || 0}</div>
            </div>
            <div className="bg-white p-4 rounded shadow-sm">
              <div className="text-gray-500 text-sm">Storage Used</div>
              <div className="text-2xl font-bold">{Math.round((tenant?.storage?.usedMB || 0) / 1024 * 100) / 100} GB</div>
            </div>
            <div className="bg-white p-4 rounded shadow-sm">
              <div className="text-gray-500 text-sm">Status</div>
              <div className="text-2xl font-bold">{tenant?.isActive ? 'Active' : 'Suspended'}</div>
            </div>
          </div>
        </div>

        {/* Tab Bar */}
        <div className="px-6 py-2 bg-white border-b flex gap-2">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                activeTab === item.id
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <div className="p-6">
            <StatsGrid>
              <StatCard title="Total Users" value={users.length || tenant?.users?.length || 0} change={10} />
              <StatCard title="Active Users" value={users.filter(u => u.isActive).length || tenant?.users?.filter(u => u.isActive).length || 0} />
              <StatCard title="Storage Used" value={`${Math.round((tenant?.storage?.usedMB || 0) / 1024 * 100) / 100} GB`} />
              <StatCard title="Credits" value={balance?.available || 0} />
            </StatsGrid>

            <AnalyticsGrid>
              <LineChartWidget title="User Growth" data={generateMockData(14)} xKey="date" yKeys={['users']} />
              <PieChartWidget title="User Status" data={[
                { name: 'Active', value: users.filter(u => u.isActive).length || tenant?.users?.filter(u => u.isActive).length || 0 },
                { name: 'Inactive', value: users.filter(u => !u.isActive).length || tenant?.users?.filter(u => !u.isActive).length || 0 }
              ]} nameKey="name" valueKey="value" />
            </AnalyticsGrid>

            {/* Packages, Usage, Transactions Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <div className="bg-white rounded shadow p-4">
                <h3 className="text-lg font-semibold mb-4">Packages</h3>
                {packagesLoading ? (
                  <p className="text-gray-500 text-center py-8">Loading...</p>
                ) : packages.length > 0 ? (
                  <PieChartWidget 
                    data={(() => {
                      const types = {};
                      packages.forEach(pkg => {
                        const type = pkg.type || 'other';
                        types[type] = (types[type] || 0) + 1;
                      });
                      return Object.entries(types).map(([name, value]) => ({ name, value }));
                    })()} 
                    nameKey="name" 
                    valueKey="value" 
                  />
                ) : (
                  <p className="text-gray-500 text-center py-8">No packages</p>
                )}
              </div>

              <div className="bg-white rounded shadow p-4">
                <h3 className="text-lg font-semibold mb-4">Usage Stats</h3>
                {usageData ? (
                  <LineChartWidget 
                    title=""
                    data={usageData.dailyStats?.slice(-14).map(d => ({
                      date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                      apiCalls: d.apiCalls || 0,
                      users: d.users || 0
                    })) || generateMockData(14)} 
                    xKey="date" 
                    yKeys={['apiCalls', 'users']} 
                  />
                ) : (
                  <p className="text-gray-500 text-center py-8">No usage data</p>
                )}
              </div>

              <div className="bg-white rounded shadow p-4">
                <h3 className="text-lg font-semibold mb-4">Credit Transactions</h3>
                {billingLoading ? (
                  <p className="text-gray-500 text-center py-8">Loading...</p>
                ) : transactions.length > 0 ? (
                  <LineChartWidget 
                    title=""
                    data={transactions.slice(-7).reverse().map(tx => ({
                      date: new Date(tx.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                      amount: tx.amount || 0
                    }))} 
                    xKey="date" 
                    yKeys={['amount']} 
                  />
                ) : (
                  <p className="text-gray-500 text-center py-8">No transactions</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="bg-white rounded shadow overflow-hidden mb-6">
            <table className="min-w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left">User</th>
                  <th className="px-4 py-2 text-left">Role</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Added</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="5" className="px-4 py-4 text-center">Loading...</td></tr>
                ) : (users.length || tenant?.users?.length) === 0 ? (
                  <tr><td colSpan="5" className="px-4 py-4 text-center">No users. Add users to get started.</td></tr>
                ) : (
                  (users.length ? users : tenant?.users || []).map((u) => (
                    <tr key={u.userId} className="border-t">
                      <td className="px-4 py-3">{u.email}</td>
                      <td className="px-4 py-3">
                        <select
                          value={u.role}
                          onChange={(e) => handleUpdateUserRole(u.userId, e.target.value)}
                          className="border rounded px-2 py-1"
                        >
                          <option value="owner">Owner</option>
                          <option value="admin">Admin</option>
                          <option value="manager">Manager</option>
                          <option value="user">User</option>
                          <option value="viewer">Viewer</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-sm ${u.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {u.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">{new Date(u.addedAt).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleRemoveUser(u.userId)}
                          className="text-red-600 hover:underline"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
  

      {activeTab === 'usage' && (
        <div className="p-6">
          <div className="bg-white rounded shadow p-6">
            <h2 className="text-xl font-bold mb-4">Organization Usage</h2>
            <div className="grid grid-cols-2 gap-6">
              <div className="p-4 bg-gray-50 rounded">
                <div className="text-gray-500 text-sm">API Calls</div>
                <div className="text-3xl font-bold">12,450</div>
                <div className="text-green-600 text-sm mt-1">↑ 5% from last month</div>
              </div>
              <div className="p-4 bg-gray-50 rounded">
                <div className="text-gray-500 text-sm">Storage Used</div>
                <div className="text-3xl font-bold">450 MB</div>
                <div className="text-gray-500 text-sm mt-1">of 1 GB available</div>
              </div>
              <div className="p-4 bg-gray-50 rounded">
                <div className="text-gray-500 text-sm">Active Users</div>
                <div className="text-3xl font-bold">{tenant?.users?.length || 0}</div>
                <div className="text-gray-500 text-sm mt-1">users</div>
              </div>
              <div className="p-4 bg-gray-50 rounded">
                <div className="text-gray-500 text-sm">Bandwidth</div>
                <div className="text-3xl font-bold">1.2 GB</div>
                <div className="text-gray-500 text-sm mt-1">this month</div>
              </div>
            </div>
          </div>
        </div>
      )}

       {activeTab === 'billing' && (
         <div className="p-6">
           <div className="bg-white rounded shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Billing & Credits</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setCreditMode('transfer'); setShowCreditModal(true); }}
                    className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
                  >
                    + Add Credits
                  </button>
                  <button
                    onClick={() => setShowTransferModal(true)}
                    className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
                  >
                    Transfer to User
                  </button>
                </div>
              </div>
             {billingLoading ? (
               <div className="text-center py-8">Loading billing data...</div>
             ) : (
               <>
                 <div className="grid grid-cols-3 gap-6 mb-6">
                   <div className="p-4 bg-green-50 rounded border border-green-200">
                     <div className="text-green-600 text-sm">Available Credits</div>
                     <div className="text-3xl font-bold text-green-700">{balance?.available || 0} {balance?.currency || 'INR'}</div>
                     <div className="text-green-600 text-sm">Balance: {balance?.balance || 0}</div>
                   </div>
                   <div className="p-4 bg-blue-50 rounded border border-blue-200">
                     <div className="text-blue-600 text-sm">Spent</div>
                     <div className="text-3xl font-bold text-blue-700">{balance?.spent || 0}</div>
                     <div className="text-blue-600 text-sm">Total spent</div>
                   </div>
                   <div className="p-4 bg-purple-50 rounded border border-purple-200">
                     <div className="text-purple-600 text-sm">Current Plan</div>
                     <div className="text-3xl font-bold text-purple-700">{tenant?.subscription?.plan || 'Free'}</div>
                   </div>
                 </div>

                 <div className="mb-6">
                   <h3 className="text-lg font-semibold mb-3">Recent Transactions</h3>
                   {transactions.length === 0 ? (
                     <p className="text-gray-500">No transactions yet.</p>
                   ) : (
                     <div className="bg-gray-50 rounded overflow-hidden">
                       <table className="min-w-full">
                         <thead className="bg-gray-100">
                           <tr>
                             <th className="px-4 py-2 text-left">Type</th>
                             <th className="px-4 py-2 text-left">Amount</th>
                             <th className="px-4 py-2 text-left">Description</th>
                             <th className="px-4 py-2 text-left">Date</th>
                           </tr>
                         </thead>
                         <tbody>
                           {transactions.map((tx) => (
                             <tr key={tx._id} className="border-t">
                               <td className="px-4 py-2">
                                 <span className={`px-2 py-1 rounded text-xs ${tx.type === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                   {tx.type}
                                 </span>
                               </td>
                               <td className="px-4 py-2 font-medium">{tx.amount} {balance?.currency || 'INR'}</td>
                               <td className="px-4 py-2 text-sm">{tx.description || '-'}</td>
                               <td className="px-4 py-2 text-sm">{new Date(tx.createdAt).toLocaleDateString()}</td>
                             </tr>
                           ))}
                         </tbody>
                       </table>
                     </div>
                   )}
                 </div>
               </>
             )}
           </div>
         </div>
       )}

      <CreditTransferModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        mode="transfer"
        userRole="clientadmin"
        userContext={{ tenantId: tenantId }}
      />

      {showUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-6">
          <div className="bg-white p-6 rounded-lg w-full max-w-md my-6">
            <h2 className="text-xl font-bold mb-4">Add User to Client</h2>
            <form onSubmit={handleAddUser}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="John Doe"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Password *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Min 6 characters"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="manager">Manager</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowUserModal(false)} className="px-4 py-2 border rounded">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded">
                  Add User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSettingsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-6">
          <div className="bg-white p-6 rounded-lg w-full max-w-md my-6">
            <h2 className="text-xl font-bold mb-4">Client Settings</h2>
            <form onSubmit={handleUpdateSettings}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Client Name</label>
                <input
                  type="text"
                  value={settings.name}
                  onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Contact Email</label>
                <input
                  type="email"
                  value={settings.contactEmail}
                  onChange={(e) => setSettings({ ...settings, contactEmail: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Phone</label>
                <input
                  type="text"
                  value={settings.phone}
                  onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div className="p-3 bg-gray-50 rounded mb-4">
                <button
                  type="button"
                  onClick={handleStatusToggle}
                  className={`w-full py-2 rounded ${tenant?.isActive ? 'bg-red-600 text-white' : 'bg-green-600 text-white'}`}
                >
                  {tenant?.isActive ? 'Suspend Client' : 'Activate Client'}
                </button>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowSettingsModal(false)} className="px-4 py-2 border rounded">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      
        )}

       {showCreditModal && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-6">
           <div className="bg-white p-6 rounded-lg w-full max-w-md my-6">
             <h2 className="text-xl font-bold mb-4">Manage Credits</h2>
             <div className="mb-4">
               <label className="block text-sm font-medium mb-1">Current Balance</label>
               <div className="text-2xl font-bold text-green-600">₹{balance?.available || '0'}</div>
             </div>
             <div className="mb-4">
               <label className="block text-sm font-medium mb-2">Select Credit Pack</label>
               <select 
                 className="w-full border rounded px-3 py-2" 
                 value={topupAmount} 
                 onChange={(e) => setTopupAmount(parseInt(e.target.value))}
               >
                 <option value={500}>₹500 - Intro Pack</option>
                 <option value={5000}>₹5,000 - Basic Pack</option>
                 <option value={10000}>₹10,000 - Bronze Pack</option>
                 <option value={50000}>₹50,000 - Silver Pack</option>
                 <option value={100000}>₹100,000 - Gold Pack</option>
                 <option value={1000000}>₹1,000,000 - Platinum Pack</option>
               </select>
             </div>
             <div className="mb-4">
               <label className="block text-sm font-medium mb-2">Payment Method</label>
               <div className="grid grid-cols-2 gap-2">
                 <button 
                   type="button" 
                   className="p-3 border rounded text-center hover:bg-gray-50" 
                   onClick={() => handleTopup('razorpay')}
                 >💳 Razorpay</button>
                 <button 
                   type="button" 
                   className="p-3 border rounded text-center hover:bg-gray-50" 
                   onClick={() => handleTopup('stripe')}
                 >💳 Stripe</button>
               </div>
             </div>
             <div className="flex gap-2">
               <button onClick={() => setShowCreditModal(false)} className="bg-gray-600 text-white px-4 py-2 rounded">Close</button>
             </div>
           </div>
         </div>
       )}

    </div>
      </div>
    </div>
  );
}